import React, { useEffect, useState } from 'react';
import { Form, Row, Col, FormInstance, Button, Tabs, Space, message, Steps, StepProps, notification } from 'antd';
import { MasterConfigs } from '../../interfaces/entity';
import RenderFormItem from './FormItems';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { postData, putData } from '../../actions/developer';
import { getData } from '../../actions/apiCall';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
const { useForm } = Form;

interface RenderForm {
    formInstance?: (form: FormInstance) => void;
    onClose?: (status?: boolean) => void
    config: MasterConfigs;
    id?: string;
    isView?: boolean;
    setLoading?: (e) => void
    stepsIndex?: (e: { current: number, lastIndex: number }) => void
    previousPage?: number;
}

export const transformData = (data) => {
    if (Array.isArray(data)) {
        return data.map((item) => transformData(item)); // Recursively handle arrays
    }

    if (typeof data === 'object' && data !== null) {
        return Object.entries(data).reduce((acc, [key, value]) => {
            if (typeof value === 'string' && !/^\d+$/.test(value) && moment(value, moment.ISO_8601, true).isValid()) {
                acc[key] = dayjs(value);
            } else {
                acc[key] = transformData(value);
            }
            return acc;
        }, {});
    }

    return data;
};

const RenderForm: React.FC<RenderForm> = ({
    formInstance,
    config,
    onClose,
    id: propsId,
    isView,
    setLoading,
    stepsIndex,
    previousPage
}) => {
    const [form] = useForm();
    const [activeID, setActiveID] = useState(null);
    const [current, setCurrent] = useState(0);
    const [id, setId] = useState(propsId);
    const [stepsItems, setStepsItems] = useState(() => {
        if (config?.getFormColumns && typeof config.getFormColumns === 'function') {
            const columns = config.getFormColumns();
            return columns ? columns.map((item, index) => ({ title: item?.tabName, key: index })) : [];
        }
        return [];
    });
    const navigate = useNavigate()

    const dispatch = useDispatch()

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlId = searchParams.get(`formId`)
        setId(urlId)
    }, [current])

    useEffect(() => {
        setId(propsId)
        if (propsId) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set(`formId`, propsId);
            navigate({ search: searchParams.toString() });
        }
    }, [propsId])

    useEffect(() => {
        if (previousPage >= 0) {
            setCurrent(previousPage)
        }
    }, [previousPage])

    useEffect(() => {
        if (form && formInstance) {
            formInstance(form)
        }
    }, [form, config])

    useEffect(() => {
        if (config?.getFormColumns && typeof config.getFormColumns === 'function') {
            const formColumns = config.getFormColumns();
            formColumns.forEach(ele => {
                if (ele.type == 'form') {
                    const currentValue = form.getFieldValue(ele.name);
                    if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
                        form.setFieldsValue({ [ele.name]: [{}] })
                    }
                }
            })
        }
    }, [config, form])

    // Set initial form values when creating a new record (no id)
    useEffect(() => {
        if (!id && config?.setInitialForm) {
            const initialValues = config.setInitialForm();
            if (initialValues && typeof initialValues === 'object' && Object.keys(initialValues).length > 0) {
                form.setFieldsValue(initialValues);
            }
        }
    }, [id, config, form])

    useEffect(() => {
        if (id) {
            patchData(id);
        }
    }, [id])

    const getIds = (data: Record<string, any>) => {
        const ids: Record<string, any> = {};
        Object.keys(data).forEach((key) => {
            if (data[key] && typeof data[key] === 'object' && '_id' in data[key]) {
                ids[key] = data[key]._id;
            } else {
                ids[key] = data[key];
            }
        });
        return ids;
    }


    const patchData = async (_id) => {
        if (config?.api && _id) {
            setLoading(true);
            try {
                const resData = await dispatch(getData(config?.api() + '/' + _id));
                if (resData.code == 200) {
                    // Call onDataLoad BEFORE setting form values to ensure state is set correctly
                    // This allows the form to determine which fields to show before populating data
                    if (config?.onDataLoad) {
                        config.onDataLoad(resData.data);
                        // Small delay to ensure state updates propagate before setting form values
                        await new Promise(resolve => setTimeout(resolve, 50));
                    }
                    form.setFieldsValue(getIds(transformData(resData.data)));
                } else {
                    message.error('Failed to load data')
                }
            } catch (error) {
                console.error('Error loading form data:', error);
                message.error('Failed to load data')
            } finally {
                setLoading(false);
            }
        }
    }

    const patchDataForTabs = async (api, prePopulatd, id) => {
        const resData = await dispatch(getData(id ? (config?.api() + '/' + id) : (api ?? config?.api()), prePopulatd));
        if (resData.code == 200) {
            form.setFieldsValue(transformData(resData.data));
            config?.onDataLoad && config.onDataLoad(resData.data)
            setActiveID(resData.data?._id)
        } else {
            setActiveID(null)
        }
    }

    const onRefresh = () => {
        if (config?.onRefresh) {
            config.onRefresh()
        }
        dispatch({
            type: 'REFRESH_TABLE',
            refreshTable: true,
        })
    }

    const refreshAndCancel = () => {
        if (config?.isSteps && current < stepsItems.length - 1) {
            next();
        } else {
            onRefresh()
            onCancel(config?.isSteps ? current < stepsItems.length - 1 ? false : true : false)
        }
    }

    const onSubmit = async (values, api = null, apiParams = {}) => {

        setLoading && setLoading(true)
        if (config?.onSubmit) {
            const resData = await config?.onSubmit(id, values);
            if (resData.code == 200) {
                setLoading && setLoading(false)
                message.success('Successfully uploaded')
                if (config.afterSubmit) {
                    config.afterSubmit(resData.data)
                }
                refreshAndCancel();
                return;
            } else {
                setLoading && setLoading(false)
                if (config.onError) {
                    config.onError(resData)
                }
                notification.error({
                    type: 'error',
                    description: resData.message || 'Failed to submit',
                    message: 'Failed to Upload'
                })
            }
        } else {
            if (api || config?.api) {
                if (id || activeID) {
                    const resData = await dispatch(putData((api ?? config.api()) + '/' + (id || activeID), { ...values, ...config.apiParams, ...apiParams }));
                    if (resData) {
                        message.success('Successfully updated')
                        refreshAndCancel();
                        setLoading && setLoading(false)
                        if (config.afterSubmit)
                            config.afterSubmit(resData)
                        if (config?.isSteps && current < stepsItems.length - 1) {
                            const searchParams = new URLSearchParams(location.search);
                            searchParams.set(`formId`, resData._id);
                            navigate({ search: searchParams.toString() });
                        }
                    } else {
                        setLoading && setLoading(false)
                        notification.error({
                            type: 'error',
                            description: resData.message || 'Failed to updated',
                            message: 'Failed to Upload'
                        })
                        if (config.onError)
                            config.onError(resData)
                    }
                } else {
                    const resData = await dispatch(postData((api ?? config.api()), { ...values, ...config.apiParams, ...apiParams }));
                    if (resData.code == 200) {
                        setLoading && setLoading(false)
                        message.success('Successfully uploaded')
                        if (config.afterSubmit)
                            config.afterSubmit(resData.data)
                        if (config?.isSteps && current < stepsItems.length - 1) {
                            const searchParams = new URLSearchParams(location.search);
                            searchParams.set(`formId`, resData?.data?._id);
                            navigate({ search: searchParams.toString() });
                        }
                        refreshAndCancel();
                    } else if (resData.code == 301) {
                        setLoading && setLoading(false)
                        if (config.onError)
                            config.onError(resData)
                        notification.error({
                            type: 'error',
                            description: resData.message,
                            message: 'Failed to Upload'
                        })
                    } else {
                        setLoading && setLoading(false)
                        if (config.onError)
                            config.onError(resData)
                        notification.error({
                            type: 'error',
                            description: resData.message || 'Failed to updated',
                            message: 'Failed to Upload'
                        })
                    }
                }
            }
        }
    }

    const onCancel = (status = false) => {
        if (onClose) {
            onClose(status)
        }
    }

    useEffect(() => {
        if (current >= 0 && config?.isSteps && config?.getFormColumns && typeof config.getFormColumns === 'function') {
            if (stepsIndex) stepsIndex({ current, lastIndex: stepsItems.length - 1 })
            form.resetFields();
            if (formInstance) formInstance(form);
            const formColumns = config.getFormColumns();
            const formSetup = formColumns && formColumns[current];
            formSetup?.getChiledForm().forEach(ele => {
                if (ele.type == 'form') {
                    form.setFieldValue(ele.name, [{}])
                }
            })
            if (formSetup.prepoluate || id) {
                patchDataForTabs(formSetup?.api(null), formSetup.prepoluate, id)
            }
        }
    }, [current])


    const next = () => {
        setCurrent(current + 1);
    };

    const renderNestedForm = (nestedFields, combo) => (
        <Form.List name={nestedFields.name}>
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={[16, 16]} key={key}>
                            <Col span={config?.hideAddMoreButton ? 24 : 20} >
                                <div className='border-box'>
                                    <Row gutter={[16, 16]} key={key}>
                                        {nestedFields.form.map((field) => (
                                            <Col span={field.span ?? 12} key={field.name}>
                                                <RenderFormItem column={{ ...field, name: [name, field.name], ...restField }} combo={combo} form={form} />
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </Col>
                            {!config?.hideAddMoreButton && <Col span={4}>
                                <Space>
                                    <Button danger type="dashed" onClick={() => remove(name)}>
                                        <MinusCircleOutlined />
                                    </Button>
                                    <Button type="dashed" onClick={() => add()} >
                                        <PlusCircleOutlined />
                                    </Button>
                                </Space>
                            </Col>}
                        </Row>
                    ))}
                </>
            )}
        </Form.List>
    );


    const getStepsForm = (tab) => {
        let props = {};

        if (config.restrictDirectJump) {
            props = {}
        } else {
            props = { onChange: (e) => setCurrent(e) }
        }

        return <>
            <Space style={{ width: '100%' }} direction='vertical'>
                <Steps current={current} items={stepsItems} {...props} {...config?.stepesProps} />
                <Form
                    layout='vertical'
                    onValuesChange={(e, values) => {
                        config?.handleValuesChange && config.handleValuesChange(e, values)
                    }}
                    form={form}
                    onFinish={(value) => onSubmit(value, tab?.api(id), tab?.apiParams)}
                    disabled={isView}
                    requiredMark={!isView}
                    {...config?.formProps}
                >
                    <Row gutter={[16, 16]} key={`${tab.tabName}`}>
                        {tab?.getChiledForm().map((column, index) => (<Col key={`${index}`} span={column?.span ?? 8}>
                            {column?.type !== 'form' ? <React.Fragment key={column?.name}>
                                <RenderFormItem column={column} combo={config.combo} form={form} />
                            </React.Fragment> :
                                <React.Fragment key={column?.name}>
                                    {renderNestedForm(column, config.combo)}
                                </React.Fragment>
                            }
                        </Col>
                        ))}
                    </Row>
                </Form>
            </Space>
        </>
    }


    return (
        <Row gutter={[16, 16]}>
            <Col span={24}>
                {config?.isSteps && config?.getFormColumns && getStepsForm(config.getFormColumns()[current])}
                {!config?.isSteps && <Form
                    key={config?.getFormColumns ? JSON.stringify(config.getFormColumns().map(c => c.name || c.type)) : 'form'}
                    layout='vertical'
                    form={form}
                    onValuesChange={(e, values) => {
                        config?.handleValuesChange && config.handleValuesChange(e, values)
                    }}
                    onFinish={onSubmit}
                    disabled={isView}
                    requiredMark={!isView}
                    {...config?.formProps}
                >
                    <Row gutter={[16, 16]}>
                        {config?.getFormColumns && typeof config.getFormColumns === 'function' ? config.getFormColumns().map((column) => (
                            <Col span={column?.span ?? 8}>
                                {column?.type !== 'form' ? <React.Fragment key={column?.name}>
                                    <RenderFormItem column={column} combo={config.combo} form={form} />
                                </React.Fragment> :
                                    <React.Fragment key={column?.name}>
                                        {renderNestedForm(column, config.combo)}
                                    </React.Fragment>
                                }
                            </Col>
                        )) : null}
                    </Row>
                </Form>}
            </Col>
        </Row >
    );
};

export default RenderForm;